import { useState, FormEvent } from 'react'
import { ContactFormData } from '../types'
import './Contact.css'

const Contact = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setErrorMessage('')

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })
      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        throw new Error(data.error || 'Failed to send message')
      }
      setSubmitStatus('success')
      setFormData({ name: '', email: '', message: '' })
    } catch (error) {
      console.error('Error submitting form:', error)
      setSubmitStatus('error')
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="section-header">
          <span className="section-eyebrow">We're here</span>
          <h2>Begin with a conversation</h2>
          <p className="section-subtitle">
            If you are in the middle of a loss, or simply ready to begin, write to us.
            We will answer with care. There is no obligation.
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <h3>Reach us directly</h3>
            <p className="contact-note">A person reads every note. If speaking is easier than writing, the phone is open.</p>
            <div className="contact-details">
              <div className="contact-detail-item">
                <span className="contact-label">Email</span>
                <a href="mailto:hello@echocares.info" className="contact-value">
                  hello@echocares.info
                </a>
              </div>
              <div className="contact-detail-item">
                <span className="contact-label">Phone</span>
                <a href="tel:+13364376147" className="contact-value">
                  (336) 437-6147
                </a>
              </div>
              <div className="contact-detail-item">
                <span className="contact-label">Website</span>
                <a href="https://www.echocares.info" className="contact-value">
                  www.echocares.info
                </a>
              </div>
            </div>
          </div>
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Your name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                autoComplete="name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="message">What would you like us to know?</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={formData.message}
                onChange={handleChange}
                required
              />
            </div>
            {submitStatus === 'success' && (
              <div className="form-message success">
                Thank you. Your note is with us, and we will reply with care.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="form-message error">
                {errorMessage || "Something went wrong. Please try again or contact us directly."}
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Send your note'}
            </button>
          </form>
        </div>
      </div>
    </section>
  )
}

export default Contact

